import { postData } from '../utils/ajax-utils';
import { SuggestionListElement } from './select-text-suggestions';

export function initCsrForm() : void {
    document.querySelectorAll<HTMLElement>('.js-csr-form').forEach($csfForm => {
        new CsrForm($csfForm);
    });
}

class CsrForm {
    $container: HTMLElement;
    $generationMethodRadios: NodeListOf<HTMLInputElement>;
    $showIfGenerationMethodIsNo: HTMLElement;
    $csrInput: HTMLInputElement|null;
    $csrInputError: HTMLElement|null;
    $verifyButton: HTMLButtonElement;
    $useClmPartnerCsrButtons: NodeListOf<HTMLButtonElement>;
    $csrFqdnInput: HTMLInputElement;
    $csrCNInput: HTMLInputElement;
    $csrSubmit: HTMLButtonElement;
    $csrFunctionInput: HTMLInputElement;
    $csrEmailInput: HTMLInputElement;
    $csrDocumentTypeSelect: HTMLSelectElement;
    $showIfGenerationMethodIsYes: HTMLElement;
    $csrGenerateFunctionInput: HTMLInputElement|null;
    $csrGenerateEmailInput: HTMLInputElement|null;
    $csrDomainFields: NodeListOf<SuggestionListElement>;
    $formSubmit: HTMLButtonElement;
    csrIsValid: boolean;

    constructor($container: HTMLElement) {
        this.$container = $container;
        this.$generationMethodRadios = this.$container.querySelectorAll<HTMLInputElement>('.js-csr-form-generation-method input');
        this.$showIfGenerationMethodIsNo = this.$container.querySelector('.js-csr-form-show-if-generation-method-is-no') as HTMLElement;
        this.$csrInput = this.$container.querySelector<HTMLInputElement>('.js-csr-form-csr');
        this.$csrInputError = this.$csrInput?.closest('.form-group')?.querySelector<HTMLElement>('.form-error') ?? null;
        this.$verifyButton = this.$container.querySelector('.js-csr-form-verify-button') as HTMLButtonElement;
        this.$useClmPartnerCsrButtons = this.$container.querySelectorAll<HTMLButtonElement>('.js-csr-form-use-clm-partner-csr-button');
        this.$csrFqdnInput = this.$container.querySelector('.js-csr-form-fqdn') as HTMLInputElement;
        this.$csrCNInput = this.$container.querySelector('.js-csr-form-cn') as HTMLInputElement;
        this.$csrSubmit = this.$container.querySelector('.js-csr-form-submit') as HTMLButtonElement;
        this.$csrFunctionInput = this.$container.querySelector('.js-csr-form-function') as HTMLInputElement;
        this.$csrEmailInput = this.$container.querySelector('.js-csr-form-email') as HTMLInputElement;
        this.$csrDocumentTypeSelect = this.$container.querySelector('.js-csr-form-document-type') as HTMLSelectElement;
        this.$showIfGenerationMethodIsYes = this.$container.querySelector('.js-csr-form-show-if-generation-method-is-yes') as HTMLElement;
        this.$csrGenerateFunctionInput = this.$container.querySelector<HTMLInputElement>('.js-csr-form-generate-function');
        this.$csrGenerateEmailInput = this.$container.querySelector<HTMLInputElement>('.js-csr-form-generate-email');
        this.$csrDomainFields = this.$container.querySelectorAll<SuggestionListElement>('.js-csr-form-domain');
        this.$formSubmit = this.$container.closest('form')?.querySelector('.js-submit-btn') as HTMLButtonElement;

        this.csrIsValid = !this.$verifyButton && ((this.$csrFqdnInput && this.$csrFqdnInput.value !== '') || ((this.$csrFunctionInput && this.$csrFunctionInput.value !== '') || (this.$csrEmailInput && this.$csrEmailInput.value !== '') || (this.$csrDocumentTypeSelect && this.$csrDocumentTypeSelect.value !== '')));

        // Form events
        this.onGenerationMethodChange();
        
        this.$generationMethodRadios.forEach($radio => {
            $radio.addEventListener('change', () => this.onGenerationMethodChange());
        });

        this.$useClmPartnerCsrButtons.forEach($partnerCsrButton => {
            const csrOfClmPartner = $partnerCsrButton.dataset.csr || '';
            $partnerCsrButton.removeAttribute('data-csr');

            $partnerCsrButton.addEventListener('click', () => {
                if (this.$csrInput) {
                    this.$csrInput.value = csrOfClmPartner;
                    this.$verifyButton?.click();
                }
            });
        });

        if (this.$verifyButton) {
            this.$verifyButton.addEventListener('click', () => this.verifyCsr());
        }

        if (this.$csrInput) {
            this.$container.closest('form')?.addEventListener('submit', () => {
                if (this.$csrInput && !this.csrIsValid) {
                    this.$csrInput.value = '';
                }
            });
        }

        // Submit the form (not the CSR) when pressing the "Enter" key in a domain field
        this.$csrDomainFields.forEach($field => {
            if ($field.value.length === 0 && $field.dataset.autofill) {
                $field.value = $field.dataset.autofill;
            }

            $field.addEventListener('keydown', evt => {
                if ('Enter' === evt.key && !$field.$suggestionsList?.querySelector('.active')) {
                    evt.preventDefault();

                    this.$formSubmit.click();
                }
            });
        });

        // Fill the csrGenerateInputs if not already filled
        if (this.$csrGenerateFunctionInput?.dataset.value && !this.$csrGenerateFunctionInput.value) {
            this.$csrGenerateFunctionInput.value = this.$csrGenerateFunctionInput.dataset.value;
        }
        if (this.$csrGenerateEmailInput?.dataset.value && !this.$csrGenerateEmailInput.value) {
            this.$csrGenerateEmailInput.value = this.$csrGenerateEmailInput.dataset.value;
        }
    }

    /**
     * @return {void}
     */
    onGenerationMethodChange() : void {
        this.$generationMethodRadios.forEach($radio => {
            if (!$radio.checked) {
                return;
            }

            if ('yes' === $radio.value) {
                this.$showIfGenerationMethodIsYes.classList.remove('hidden');
                this.$showIfGenerationMethodIsNo.classList.add('hidden');
                // Prevent HTML5 validation errors
                this.$showIfGenerationMethodIsYes.querySelector('#certificate_form_csr_generate_function')?.removeAttribute('disabled');
                this.$showIfGenerationMethodIsNo.querySelector('#certificate_form_csr_function')?.setAttribute('disabled', 'true');
            } else {
                this.$showIfGenerationMethodIsNo.classList.remove('hidden');
                this.$showIfGenerationMethodIsYes.classList.add('hidden');
                // Prevent HTML5 validation errors
                this.$showIfGenerationMethodIsNo.querySelector('#certificate_form_csr_function')?.removeAttribute('disabled');
                this.$showIfGenerationMethodIsYes.querySelector('#certificate_form_csr_generate_function')?.setAttribute('disabled', 'true');
            }
        });
    }

    /**
     * @return {void}
     */
    verifyCsr() : void {
        if (!this.$csrInput || !this.$csrInputError) {
            return;
        }

        if ('' === this.$csrInput.value) {
            this.$csrInputError.innerHTML = `<li>${this.$csrInput.dataset.invalidMessage}</li>`;
            this.$csrInputError.closest('.form-group')?.classList.add('has-error');
        } else {
            this.$verifyButton.setAttribute('disabled', 'true');
            this.$verifyButton.querySelector('.loading')?.classList.remove('hidden');
            this.$useClmPartnerCsrButtons.forEach($csrButton => {
                $csrButton.setAttribute('disabled', 'true');
                $csrButton.querySelector('.loading')?.classList.remove('hidden');
            });

            this.$csrInputError.innerHTML = '';
            this.$csrInputError.closest('.form-group')?.classList.remove('has-error');

            postData(scriptVar.ajaxUrl, {
                action: 'verify_csr',
                order_guid: this.$csrInput.dataset.orderGuid ?? '',
                csr: this.$csrInput.value,
            }).then(response => {
                this.$verifyButton.removeAttribute('disabled');
                this.$verifyButton.querySelector('.loading')?.classList.add('hidden');
                this.$useClmPartnerCsrButtons.forEach($csrButton => {
                    $csrButton.removeAttribute('disabled');
                    $csrButton.querySelector('.loading')?.classList.add('hidden');
                });

                if (!this.$csrInput || !this.$csrInputError) {
                    return;
                }

                if (response.success) {
                    this.csrIsValid = true;

                    this.$csrInput.setAttribute('readonly', 'true');
                    if (this.$csrFunctionInput) {
                        // SWC-17: Prefix the function input with the holder entity name
                        const $entityNameIfYes = document.querySelector<HTMLInputElement>('.js-requested-entity-name-if-yes');
                        const $entityNameIfNo = document.querySelector<HTMLInputElement>('.js-requested-entity-name-if-no');
                        let functionPrefix = '';

                        if ($entityNameIfYes?.checkVisibility()) {
                            functionPrefix = $entityNameIfYes.value + ' - ';
                        } else if ($entityNameIfNo?.checkValidity() && $entityNameIfNo.value) {
                            functionPrefix = $entityNameIfNo.value + ' - ';
                        }

                        this.$csrFunctionInput.value = this.$csrFunctionInput.dataset.value ?? (functionPrefix + response.data.cn);
                    }
                    if (this.$csrEmailInput) {
                        const csrEmailValue = this.$csrEmailInput.dataset.value ?? (response.data.emails.length > 0 ? response.data.emails[0] : false);

                        if (csrEmailValue) {
                            this.$csrEmailInput.value = this.$csrEmailInput.dataset.value ?? response.data.emails[0];
                        }
                    }

                    if (this.$csrFqdnInput && this.$csrCNInput) {
                        this.$csrFqdnInput.value = JSON.stringify(response.data.fqdn);
                        this.$csrCNInput.value = response.data.cn;

                        const $formSubmitBtns = this.$container.closest('form')?.querySelectorAll('[type="submit"]');
                        $formSubmitBtns?.forEach($btn => {
                            if (this.$csrSubmit !== $btn) {
                                $btn.setAttribute('disabled', 'true');
                            }
                        });
                        
                        this.$csrSubmit.click();
                    }

                    if (this.$csrDocumentTypeSelect?.dataset.value) {
                        this.$csrDocumentTypeSelect.value = this.$csrDocumentTypeSelect.dataset.value;
                    }

                    this.$verifyButton.classList.add('hidden');
                    this.$useClmPartnerCsrButtons.forEach($csrButton => $csrButton.classList.add('hidden'));

                    if (this.$csrFunctionInput) {
                        this.$csrFunctionInput.closest('.form-group')?.classList.remove('hidden');
                    }
                    if (this.$csrEmailInput) {
                        this.$csrEmailInput.closest('.form-group')?.classList.remove('hidden');
                    }
                    if (this.$csrDocumentTypeSelect) {
                        this.$csrDocumentTypeSelect.closest('.form-group')?.classList.remove('hidden');
                    }
                } else {
                    const errorMessage = response.data.error || this.$csrInput.dataset.invalidMessage;
                    this.$csrInputError.innerHTML = `<li>${errorMessage}</li>`;
                    this.$csrInputError.closest('.form-group')?.classList.add('has-error');

                    this.$csrInput.value = '';
                }
            });
        }
    }
}
